import React, { useState, useEffect } from 'react';
import { Dashboard } from './Dashboard';
import { PractitionerMappingPage } from './PractitionerMappingPage';
import { StatusMappingPage } from './StatusMappingPage';
import { AppointmentTypeMappingPage } from './AppointmentTypeMappingPage';
import { ServiceMappingPage } from './ServiceMappingPage';
import { LocationConfigPage } from './LocationConfigPage';
import { SyncTrackingPage } from './SyncTrackingPage';
import { SyncAnalyticsPage } from './SyncAnalyticsPage';
import { UserManagementPage } from './UserManagementPage';
import { LocationProvider, useLocationContext } from '../context/LocationContext';
import { EntityStatsProvider, type EntityKey } from '../context/EntityStatsContext';
import { SyncTrackingProvider } from '../context/SyncTrackingContext';
import { AppShell } from '../components/layout/AppShell';
import type { SidebarKey } from '../components/layout/Sidebar';
import { getIsAdmin, hasFullPracticeAccess } from '../api/authApi';

type PageView = 'home' | 'providers' | 'status' | 'appointments' | 'services' | 'tracking' | 'analytics' | 'location-config' | 'user-management';

const HASH_TO_VIEW: Record<string, PageView> = {
    providers: 'providers',
    status: 'status',
    appointments: 'appointments',
    services: 'services',
    tracking: 'tracking',
    analytics: 'analytics',
    'location-config': 'location-config',
    'user-management': 'user-management',
};

interface Route {
    view: PageView;
    status: string;
}

function parseHash(): Route {
    const raw = window.location.hash.slice(1).replace(/^\//, '');
    const [path, queryStr] = raw.split('?');
    let view = HASH_TO_VIEW[path] ?? 'home';
    // Route guards: hide ops screens the caller isn't allowed to open via URL.
    if (view === 'tracking' && !hasFullPracticeAccess()) view = 'analytics';
    if (view === 'user-management' && !getIsAdmin()) view = 'home';
    const status = new URLSearchParams(queryStr || '').get('status') || '';
    return { view, status };
}

const HomePageInner: React.FC = () => {
    const [route, setRoute] = useState<Route>(parseHash());
    const { currentLocation } = useLocationContext();
    // Service mapping does not apply when AV EMR is the practice's CRM.
    const servicesHidden = currentLocation?.crm === 'av_emr';
    const currentView = route.view === 'services' && servicesHidden ? 'home' : route.view;
    const canSeeTracking = hasFullPracticeAccess();

    useEffect(() => {
        const onHashChange = () => setRoute(parseHash());
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, []);

    const navigate = (view: PageView, status?: string) => {
        if (view === 'tracking' && !canSeeTracking) {
            view = 'analytics';
            status = undefined;
        }
        if (view === 'user-management' && !getIsAdmin()) {
            view = 'home';
            status = undefined;
        }
        const query = status ? `?status=${encodeURIComponent(status)}` : '';
        window.location.hash = view === 'home' ? '' : `/${view}${query}`;
        setRoute({ view, status: status ?? '' });
    };

    const handleSidebarNavigate = (key: SidebarKey) => {
        if (key === 'dashboard') navigate('home');
        else if (key === 'settings') navigate('location-config');
        else navigate(key);
    };

    const sidebarActive: SidebarKey =
        currentView === 'home' ? 'dashboard' :
        currentView === 'location-config' ? 'settings' :
        currentView;

    let content: React.ReactNode;

    // Key the mapping pages by view+status so navigating from a dashboard badge
    // (e.g. "14 pending") remounts the page with that filter pre-applied.
    const mappingKey = `${currentView}-${route.status}`;

    if (currentView === 'providers') {
        content = <PractitionerMappingPage key={mappingKey} initialStatusFilter={route.status} />;
    } else if (currentView === 'status') {
        content = <StatusMappingPage key={mappingKey} initialStatusFilter={route.status} />;
    } else if (currentView === 'appointments') {
        content = <AppointmentTypeMappingPage key={mappingKey} initialStatusFilter={route.status} />;
    } else if (currentView === 'services') {
        content = <ServiceMappingPage key={mappingKey} initialStatusFilter={route.status} />;
    } else if (currentView === 'tracking') {
        content = canSeeTracking ? <SyncTrackingPage /> : <SyncAnalyticsPage />;
    } else if (currentView === 'analytics') {
        content = <SyncAnalyticsPage />;
    } else if (currentView === 'location-config') {
        content = <LocationConfigPage />;
    } else if (currentView === 'user-management') {
        content = getIsAdmin() ? <UserManagementPage /> : (
            <Dashboard onNavigate={(key: EntityKey, statusFilter?: string) => navigate(key, statusFilter)} />
        );
    } else {
        content = <Dashboard onNavigate={(key: EntityKey, statusFilter?: string) => navigate(key, statusFilter)} />;
    }

    return (
        <AppShell active={sidebarActive} onNavigate={handleSidebarNavigate}>
            {content}
        </AppShell>
    );
};

export const HomePage: React.FC = () => (
    <LocationProvider>
        <EntityStatsProvider>
            <SyncTrackingProvider>
                <HomePageInner />
            </SyncTrackingProvider>
        </EntityStatsProvider>
    </LocationProvider>
);
